import {useEffect, useRef} from 'react';
import {
  bottomSheetSwipeDownToCloseStyles,
  type BottomSheetVariant,
} from '../../utilStyles/sheet';
import {
  BOTTOM_SHEET_ANIMATION_DURATION_LONG,
  BOTTOM_SHEET_ANIMATION_DURATION_SHORT,
  BOTTOM_SHEET_ANIMATION_EASING,
} from './BottomSheet.constants';
import {useIsMounted} from '../../lib/useIsMounted';

const DISMISS_THRESHOLD_PX = 120;
const RESISTANCE_START_PX = 80;
const RESISTANCE_FACTOR = 0.4;

/**
 * Finds the closest scrollable parent element of the target element within the sheet.
 * The element must have overflow: auto/scroll and its content be taller/wider than the scrolling viewport.
 */
function findScrollableParentInSheet(
  target: HTMLElement,
  sheet: HTMLElement,
): HTMLElement | null {
  let element: HTMLElement | null = target;

  while (element && element !== sheet) {
    // eslint-disable-next-line @sail/ui-no-reflow
    const style = window.getComputedStyle(element);

    const hasOverflowY = ['auto', 'scroll'].includes(
      style.overflowY.toLowerCase(),
    );

    if (
      hasOverflowY &&
      // eslint-disable-next-line @sail/ui-no-reflow
      element.scrollHeight > element.clientHeight
    ) {
      return element;
    }

    // Move up to next parent
    element = element.parentElement;
  }

  return null;
}

/**
 * Applies resistance to the swipe down movement to achieve a more natural feel.
 * Beyond a certain threshold (RESISTANCE_START_PX), the resistance increases linearly.
 * This is a common mobile gesture pattern.
 */
function applyResistance(deltaY: number): number {
  if (deltaY <= RESISTANCE_START_PX) {
    return deltaY;
  }

  return (
    RESISTANCE_START_PX + (deltaY - RESISTANCE_START_PX) * RESISTANCE_FACTOR
  );
}

function setTransformAndAnimation({
  element,
  backdropElement,
  animated,
  transform,
  backdropOpacity,
}: {
  element: HTMLDivElement | null;
  backdropElement: HTMLDivElement | null;
  animated: boolean;
  transform: string;
  backdropOpacity: number;
}): void {
  if (element) {
    // Reset bottomSheetSwipeDownToCloseStyles just in case the animation engine has overridden it
    element.style.transform = bottomSheetSwipeDownToCloseStyles.transform ?? '';
    element.style.transition =
      bottomSheetSwipeDownToCloseStyles.transition ?? '';

    // By using CSS vars, even if the bottom sheet style resets are re-applied during a swipe down to close, we won't lose the transition and transform
    element.style.setProperty(
      '--bottom-sheet-swipe-down-to-close-transition',
      animated
        ? `transform ${BOTTOM_SHEET_ANIMATION_DURATION_SHORT}ms ${BOTTOM_SHEET_ANIMATION_EASING}`
        : 'none',
    );
    element.style.setProperty(
      '--bottom-sheet-swipe-down-to-close-transform',
      transform,
    );
  }

  if (backdropElement) {
    backdropElement.style.transition = animated
      ? `opacity ${BOTTOM_SHEET_ANIMATION_DURATION_SHORT}ms ${BOTTOM_SHEET_ANIMATION_EASING}`
      : 'none';
    backdropElement.style.opacity = String(backdropOpacity);
  }
}

/**
 * Add swipe-to-dismiss gesture to the bottom-sheet
 *
 * - If the sheet has no scrollable content, it can be closed with a swipe-down gesture anywhere on the sheet.
 * - If the sheet has scrollable content:
 *   - If the scrollable content is at the top (scrollTop === 0), it can be closed with a swipe-down gesture anywhere on the sheet.
 *   - If the scrollable content is not at the top, it can only be closed with a swipe-down gesture that starts outside the scrollable area (e.g., in the header).
 *
 * Only active when `bottomSheetVariant === 'bottom-sheet'` and `requestClose` is defined.
 */
export function useSwipeDownToClose(
  element: HTMLDivElement | null,
  backdropElement: HTMLDivElement | null,
  requestCloseUnref: (() => void) | undefined,
  bottomSheetVariant: BottomSheetVariant,
  hasCloseConfirmation: boolean,
): void {
  const isMounted = useIsMounted();

  const requestCloseRef = useRef(requestCloseUnref);
  useEffect(() => {
    requestCloseRef.current = requestCloseUnref;
  }, [requestCloseUnref]);

  useEffect(() => {
    if (bottomSheetVariant !== 'bottom-sheet' || !element) {
      return;
    }

    let startX = 0;
    let startY = 0;
    let currentY = 0;
    let isTouching = false;
    let isDragging = false;
    let firedSwipeToDismissStartedEvent = false;
    let isHorizontalGesture = false;
    let startedInScrollable = false;
    let wasScrolling = false;
    let scrollableElement: HTMLElement | null = null;
    let sheetHeight = 0;

    function fireSwipeToDismissStartedEvent(): void {
      if (!firedSwipeToDismissStartedEvent) {
        // This event is listened by our fork of React Aria's `usePress` to stop it from firing `onPress`, `onPressUp`, and the synthetic `click` event
        // https://git.corp.stripe.com/stripe-internal/mint/blob/master/pay-server/frontend/sail-next/packages/react-aria/src/%40react-aria/interactions/usePress.ts#L742
        element!.ownerDocument.dispatchEvent(
          new CustomEvent('sail:bottom-sheet-swipe-to-dismiss-started'),
        );
        firedSwipeToDismissStartedEvent = true;
      }
    }

    function onTouchStart(e: TouchEvent): void {
      if (e.touches.length !== 1) {
        return;
      }

      const touch = e.touches[0];

      startX = touch.clientX;
      startY = touch.clientY;
      currentY = touch.clientY;
      isDragging = false;
      firedSwipeToDismissStartedEvent = false;
      isHorizontalGesture = false;
      wasScrolling = false;
      isTouching = true;

      scrollableElement = findScrollableParentInSheet(
        e.target as HTMLElement,
        element!,
      );

      startedInScrollable = !!scrollableElement;

      // eslint-disable-next-line @sail/ui-no-reflow
      sheetHeight = element!.offsetHeight;
    }

    function onTouchMove(e: TouchEvent): void {
      if (!isTouching || e.touches.length !== 1) {
        return;
      }

      const touch = e.touches[0];

      currentY = touch.clientY;

      const deltaY = currentY - startY;

      // Skip horizontal gestures (could be text selection drag)
      if (isHorizontalGesture) {
        return;
      }

      if (!isDragging) {
        if (deltaY <= 0) {
          if (
            startedInScrollable &&
            !!scrollableElement &&
            // eslint-disable-next-line @sail/ui-no-reflow
            scrollableElement.scrollTop > 0
          ) {
            wasScrolling = true;
            return;
          }

          // Prevent default for upward gestures starting in a non-scrollable area (e.g., header, text input)
          if (!startedInScrollable && !e.defaultPrevented && e.cancelable) {
            e.preventDefault();
            fireSwipeToDismissStartedEvent();
          }

          return;
        }

        // Check if the scrollable content is scrolled past the top
        const scrolledPastTop =
          startedInScrollable &&
          !!scrollableElement &&
          // eslint-disable-next-line @sail/ui-no-reflow
          scrollableElement.scrollTop > 0;

        if (scrolledPastTop) {
          wasScrolling = true;
          return;
        }

        // If the user was scrolling content and reached the top, it's still a scroll,
        // or the event is not cancelable, don't start dragging
        if (wasScrolling || !e.cancelable) {
          return;
        }

        // Skip if we're zoomed in, and not at the top of the viewport
        // When the virtual keyboard opens, the viewport is sometimes zoomed in and panned.
        // In those situations, it's better to let the user scroll before allowing them to drag the sheet.
        if (
          window.visualViewport &&
          // eslint-disable-next-line @sail/ui-no-reflow
          window.visualViewport.offsetTop > 0
        ) {
          return;
        }

        // Calculate horizontal delta
        const deltaX = Math.abs(touch.clientX - startX);

        // If horizontal delta is greater than vertical delta, it's likely
        // a horizontal gesture, which could be a text selection drag or similar.
        if (deltaX > deltaY) {
          isHorizontalGesture = true;

          return;
        }

        isDragging = true;
      }

      // If another handler already claimed this event or the event is not cancelable, don't interfere
      if (e.defaultPrevented || !e.cancelable) {
        return;
      }

      // Prevent native scroll, otherwise it will trigger a bounce effect on the background
      e.preventDefault();
      fireSwipeToDismissStartedEvent();

      const visualProgress = applyResistance(Math.max(0, deltaY));
      setTransformAndAnimation({
        element,
        backdropElement,
        animated: false,
        transform: `translateY(${visualProgress}px)`,
        backdropOpacity: 1 - Math.min(1, visualProgress / sheetHeight),
      });
    }

    function snapBack(): void {
      setTransformAndAnimation({
        element,
        backdropElement,
        animated: true,
        transform: 'translateY(0)',
        backdropOpacity: 1,
      });
    }

    function onTouchEnd(e: TouchEvent): void {
      if (!isDragging || e.touches.length !== 0) {
        return;
      }

      isDragging = false;
      isTouching = false;

      const deltaY = currentY - startY;
      const requestClose = requestCloseRef.current;

      if (!requestClose || deltaY < DISMISS_THRESHOLD_PX) {
        snapBack();

        return;
      }

      if (hasCloseConfirmation) {
        snapBack();
        requestClose();

        return;
      }

      // Call requestClose immediately so parent state updates before the animation
      // reveals the behind sheet
      requestClose();

      // Animate the sheet off-screen, basically close it by moving it 100% down
      setTransformAndAnimation({
        element,
        backdropElement,
        animated: true,
        transform:
          'translateY(calc(100% + var(--sail-layer-margin-bottom, 0px)))',
        backdropOpacity: 0,
      });

      setTimeout(() => {
        if (isMounted.current) {
          snapBack();
        }

        // If the component is still mounted 40ms after bottomSheetAnimation finishes, it means that the bottom sheet wasn't closed.
        // They probably used the `requestClose` callback to update the content instead of unmounting it, so we show it again
        // 40ms is mostly chosed at random, I wanted to avoid false positives but keep the animation fast
      }, BOTTOM_SHEET_ANIMATION_DURATION_LONG + 40);
    }

    element.addEventListener('touchstart', onTouchStart);

    element.addEventListener('touchmove', onTouchMove, {
      passive: false,
    });

    // If someone stops propagation of the touch end event, the bottom sheet would stay half-open.
    // We listen to the document with capture: true to ensure we capture the event even if it's stopped down the line
    element.ownerDocument.addEventListener('touchend', onTouchEnd, {
      capture: true,
    });

    return () => {
      element.removeEventListener('touchstart', onTouchStart);
      element.removeEventListener('touchmove', onTouchMove);
      element.ownerDocument.removeEventListener('touchend', onTouchEnd, {
        capture: true,
      });

      if (isDragging) {
        // If some dependency of this useEffect changes, we need to snap back the sheet to its original position
        // otherwise, the sheet would be stuck in a half-open state
        snapBack();
      }
    };
  }, [
    element,
    backdropElement,
    bottomSheetVariant,
    hasCloseConfirmation,
    isMounted,
  ]);
}
